/* eslint-disable react/jsx-props-no-spreading */
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  memo,
  ComponentType,
  ChangeEvent,
  FocusEvent,
} from 'react';
import { TValidatorSpec } from 'core/parseValidators';
import FormContext, { IFormValue } from './FormContext';

type TCustomComponentType<TProps> = ComponentType<TProps> & {
  $$typeof?: symbol;
};
export interface IUseInputProps<D> {
  /** The name of the field in the data */
  name: string;
  /** Handles the change event for the field */
  onChange?(name: string, newValue: unknown, onBlur?: boolean): void;
  /** Handles the blur event for the field */
  onBlur?(name: string, value: unknown, isBlur?: boolean): void;
  /** Validators to run on the current field */
  validators?: TValidatorSpec[] | { [key: string]: any };
  /** Should the field be validated on blur? */
  validateOnBlur?: boolean;
  /** The default value for the field */
  defaultValue?: string;
  /** If disabled useInputAPI returns null */
  disableInputAPI?: D;
}

/**
 * Handles connecting component state to the context
 */

export type TOnChangeArgs = [ChangeEvent<HTMLInputElement>] | [string, string, boolean];

const getName = (args: TOnChangeArgs) =>
  typeof args[0] === 'string' ? args[0] : args[0].target.name;

const getValue = (args: TOnChangeArgs) =>
  typeof args[0] === 'string' ? args[1] : args[0].target.value;

export function useInputAPI<D = boolean>(props: IUseInputProps<D>, overrideFormValue?: IFormValue) {
  const formContextValue = useContext(FormContext);
  const formValue = overrideFormValue || formContextValue;
  const value = formValue?.data?.[props.name as keyof typeof formValue.data];
  const errors = formValue?.errors?.[props.name];
  const { onChange: contextOnChange, registerValidators, unregisterValidators } = formValue || {};
  const {
    name: nameProp,
    onBlur: onBlurProp,
    onChange: onChangeProp,
    validateOnBlur,
    validators,
  } = props;

  useEffect(() => {
    if (contextOnChange && props.defaultValue) {
      contextOnChange(nameProp, props.defaultValue);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (registerValidators && validators) {
      registerValidators(nameProp, validators);
    }
  }, [registerValidators, nameProp, validators]);

  useEffect(() => {
    if (unregisterValidators) {
      return () => unregisterValidators(nameProp);
    }
    return undefined;
  }, [unregisterValidators, nameProp]);

  const onChange = useCallback(
    (...args: TOnChangeArgs) => {
      // some ugly duck typing to support either (name, value) or (event) as args
      const name = getName(args);
      const newValue = getValue(args);

      onChangeProp?.(name, newValue!, args[2]);

      contextOnChange?.(name, newValue, args[2]);
    },
    [contextOnChange, onChangeProp]
  );

  const onBlur = useCallback(
    (...args: [FocusEvent<HTMLInputElement>] | [string, string, boolean]) => {
      onBlurProp?.(nameProp, value, args[2]);

      if (validateOnBlur === undefined || validateOnBlur) {
        contextOnChange?.(nameProp, value, true);
      }
    },
    [contextOnChange, nameProp, onBlurProp, validateOnBlur, value]
  );

  const inputProps = useMemo(
    () => ({
      errors,
      onBlur,
      onChange,
      value,
    }),
    [errors, onBlur, onChange, value]
  );

  return formValue && !props.disableInputAPI ? inputProps : null;
}

/**
 * Applies the result of useInputAPI to the provided component and memoizes it if needed
 */

export const withInputAPI = <TProps,>(Component: TCustomComponentType<TProps>) => {
  const Memoized =
    Component.$$typeof?.toString()?.includes('react.memo') || Component.prototype?.isReactComponent
      ? (Component as ComponentType<TProps | Omit<TProps, 'onChange' | 'onBlur'>>)
      : (memo(Component) as unknown as ComponentType<TProps | Omit<TProps, 'onChange' | 'onBlur'>>);

  return <D,>(
    props: D extends true
      ? Omit<IUseInputProps<D>, 'onChange' | 'onBlur'> & TProps
      : IUseInputProps<D> & Omit<TProps, 'onChange' | 'onBlur'>
  ) => {
    const inputProps = useInputAPI(props);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { disableInputAPI, ...rest } = props; // disableInputAPI should not get passed to DOM element.

    /** For some reason rollup won't build even though VSCode doesn't complain... */
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return <Memoized {...rest} {...inputProps} />;
  };
};
